import React, { useState, useEffect } from "react";
import { TableCell } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Check, X, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface ModalEditableCellProps {
  initialValue: string;
  isDisabled?: boolean;
  onSave: (value: string) => void;
  onRemove: () => Promise<string>;
}

const ModalEditableCell: React.FC<ModalEditableCellProps> = ({
  initialValue,
  isDisabled = false,
  onSave,
  onRemove,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState(initialValue);
  const [isRemoving, setIsRemoving] = useState(false);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const handleOpenChange = (open: boolean) => {
    if (isDisabled) return;
    if (!open) {
      setValue(initialValue);
    }
    setIsOpen(open);
  };

  const handleSave = () => {
    onSave(value);
    setIsOpen(false);
  };

  const handleRemove = async () => {
    setIsRemoving(true);
    try {
      const defaultValue = await onRemove();
      setValue(defaultValue);
      setIsOpen(false);
    } catch (error) {
      console.error("Failed to remove:", error);
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <TableCell
          className={cn(
            "py-2 px-3",
            !isDisabled && "cursor-pointer hover:bg-muted/50",
            isDisabled && "text-muted-foreground",
          )}
        >
          {initialValue}
        </TableCell>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm p-5">
        <DialogHeader className="pb-3">
          <DialogTitle className="text-lg">Edit Value</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-[auto_1fr] items-center gap-x-3 pb-5">
          <Label htmlFor="value-input" className="text-right mr-2 text-base">
            Value
          </Label>
          <Input
            id="value-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoFocus
            className="w-full h-10 text-base"
          />
        </div>
        <DialogFooter className="gap-3 sm:justify-between">
          <Button
            type="button"
            variant="destructive"
            size="icon"
            onClick={handleRemove}
            disabled={isRemoving}
            className="order-first h-10 w-10"
          >
            <Trash2 className="h-5 w-5" />
            <span className="sr-only">Delete</span>
          </Button>
          <div className="flex gap-3">
            <DialogClose asChild>
              <Button type="button" variant="outline" className="text-base h-10">
                Cancel
              </Button>
            </DialogClose>
            <Button type="button" onClick={handleSave} className="text-base h-10">
              Save changes
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModalEditableCell;
